import React, { useCallback, useEffect, useMemo, useState } from "react";
import DatePicker from "react-datepicker";
import { Form, Modal } from "react-bootstrap";
import Label from "../Label/Label";
import CustomButton from "../CustomButton/CustomButton";
import CustomDropdown from "../CustomDropdown/CustomDropdown";
import LeaveCard from "../LeaveCard/LeaveCard";
import ManagementCalendar from "./ManagementCalendar";
import HandbookPdfViewer from "./HandbookPdfViewer";
import { fetchHolidayList } from "../../services/holidayApi";
import {
  applyLeaveRequest,
  createFeedPost,
  fetchAllArRequests,
  fetchAllLeaveRequests,
  fetchArBalance,
  fetchAttendanceByDate,
  fetchAttendancePunches,
  fetchCalendarPunchesSafe,
  fetchDashboardSummary,
  fetchEmployeeMe,
  fetchFeedPosts,
  fetchHandbookDocuments,
  fetchLeaveBalance,
  fetchMyArRequests,
  fetchMyLeaveHistory,
  markAttendance,
  submitArRequest,
  updateArRequestStatus,
  updateLeaveBalanceAdmin,
  updateLeaveRequestStatus,
  updateLeaveRequestType,
  uploadHandbookDocument,
} from "../../services/managementApi";
import { getApiErrorMessage } from "../../services/apiClient";
import { isAdminUser } from "../../util/auth";
import {
  buildCalendarDayStatusMap,
  buildCalendarDayTooltipMap,
  formatDateToIso,
  formatIsoDateDisplay,
  hasLeaveBalanceDetails,
  mapArRequestTypeLabel,
  mapAttendanceStatusLabel,
  mapFeedPostForDisplay,
  mapLeaveBalanceToCards,
  mapLeaveTypeLabel,
  readFileAsDataUrl,
} from "../../util/managementHelpers";
import {
  AR_REQUEST_TYPES,
  DUMMY_HOLIDAYS,
  LEAVE_STATUS_OPTIONS,
  LEAVE_STATUS_FILTER_OPTIONS,
  LEAVE_TYPE_OPTIONS,
} from "./managementMockData";
import "./ManagementAdminTab.scss";

const FEED_POST_TYPES = [
  { id: "TEXT", value: "Text post" },
  { id: "IMAGE", value: "Image post" },
  { id: "COPY", value: "Copy / repost" },
];

const MANAGEMENT_SECTIONS = [
  { id: "leave", label: "Leave Management" },
  { id: "attendance", label: "Attendance Regularization" },
  { id: "dashboard", label: "Employee Dashboard" },
  { id: "handbook", label: "HR Handbook" },
];

const toDisplayDate = (value) => {
  if (!value) {
    return "";
  }

  const parsed = value instanceof Date ? value : new Date(value);

  if (Number.isNaN(parsed.getTime())) {
    return "";
  }

  return parsed.toLocaleDateString("en-GB");
};

const formatPunchTime = (value) => value || "--:--";

const getCalendarPunchRange = () => {
  const now = new Date();
  const fromDate = formatDateToIso(
    new Date(now.getFullYear(), now.getMonth() - 2, 1),
  );
  const toDate = formatDateToIso(
    new Date(now.getFullYear(), now.getMonth() + 3, 0),
  );

  return { fromDate, toDate };
};

const renderFeedMedia = (post) => {
  if (post.image) {
    return (
      <figure className="management_Feed_Media">
        <img src={post.image} alt={post.imageAlt || "Feed post image"} />
        {post.imageCaption && <figcaption>{post.imageCaption}</figcaption>}
      </figure>
    );
  }

  if (post.imagePlaceholder) {
    const { label, subtitle, tone = "neutral" } = post.imagePlaceholder;

    return (
      <figure className="management_Feed_Media">
        <div
          className={`management_Feed_Placeholder is-${tone}`}
          aria-label={label}
        >
          <span className="placeholder_Label">{label}</span>
          {subtitle && <span className="placeholder_Subtitle">{subtitle}</span>}
        </div>
        {post.imageCaption && <figcaption>{post.imageCaption}</figcaption>}
      </figure>
    );
  }

  return null;
};

const renderFeedPost = (post) => (
  <article
    className={`management_Feed_Post management_Feed_Post--${post.type || "text"}`}
    key={post.id}
  >
    <header className="management_Feed_Author">
      <span className="management_Feed_Avatar">{post.authorInitials || "?"}</span>
      <div>
        <p className="management_Feed_Author_Name">{post.author}</p>
        <p className="management_Feed_Meta">{post.timeAgo}</p>
      </div>
    </header>

    {post.content && <p className="management_Feed_Content">{post.content}</p>}

    {(post.type === "copy" || post.type === "COPY") && post.copyPost && (
      <blockquote className="management_Feed_Copy">
        {post.copyPost.tag && (
          <span className="management_Feed_Copy_Tag">{post.copyPost.tag}</span>
        )}
        <p className="management_Feed_Copy_Author">
          {post.copyPost.originalAuthor}
        </p>
        <p className="management_Feed_Copy_Text">{post.copyPost.originalContent}</p>
      </blockquote>
    )}

    {renderFeedMedia(post)}
  </article>
);

const ManagementAdminTab = ({ labels = {} }) => {
  const isAdmin = isAdminUser();

  const [activeSection, setActiveSection] = useState("leave");
  const [loading, setLoading] = useState(false);
  const [sectionError, setSectionError] = useState("");

  const [employeeProfile, setEmployeeProfile] = useState(null);
  const [holidays, setHolidays] = useState([]);

  const [leaveBalanceCards, setLeaveBalanceCards] = useState([]);
  const [leaveBalanceRaw, setLeaveBalanceRaw] = useState(null);
  const [adminLeaveRequests, setAdminLeaveRequests] = useState([]);
  const [adminLeaveFilterEmployeeId, setAdminLeaveFilterEmployeeId] = useState("");
  const [adminLeaveFilterStatus, setAdminLeaveFilterStatus] = useState("ALL");
  const [adminLeaveFilterStartDate, setAdminLeaveFilterStartDate] = useState(null);
  const [userLeaveHistory, setUserLeaveHistory] = useState([]);
  const [calendarPunches, setCalendarPunches] = useState([]);
  const [showApplyLeave, setShowApplyLeave] = useState(false);
  const [leaveStartDate, setLeaveStartDate] = useState(null);
  const [leaveEndDate, setLeaveEndDate] = useState(null);
  const [leaveType, setLeaveType] = useState("PAID");
  const [leaveReason, setLeaveReason] = useState("");
  const [leaveAttachment, setLeaveAttachment] = useState(null);
  const [isSubmittingLeave, setIsSubmittingLeave] = useState(false);

  const [arSelectedDate, setArSelectedDate] = useState(new Date());
  const [arRequestType, setArRequestType] = useState("BOTH");
  const [arFromDate, setArFromDate] = useState(new Date());
  const [arToDate, setArToDate] = useState(new Date());
  const [arStartHour, setArStartHour] = useState("09");
  const [arStartMinute, setArStartMinute] = useState("00");
  const [arEndHour, setArEndHour] = useState("18");
  const [arEndMinute, setArEndMinute] = useState("00");
  const [arComments, setArComments] = useState("");
  const [arAttachment, setArAttachment] = useState(null);
  const [arBalance, setArBalance] = useState(null);
  const [attendanceDayDetail, setAttendanceDayDetail] = useState(null);
  const [pendingArRequests, setPendingArRequests] = useState([]);
  const [selectedArRequest, setSelectedArRequest] = useState(null);
  const [showArModal, setShowArModal] = useState(false);
  const [isUpdatingAr, setIsUpdatingAr] = useState(false);
  const [attendancePunches, setAttendancePunches] = useState([]);
  const [showPunchHistory, setShowPunchHistory] = useState(false);
  const [isSubmittingAr, setIsSubmittingAr] = useState(false);
  const [todayAttendance, setTodayAttendance] = useState(null);
  const [isMarkingAttendance, setIsMarkingAttendance] = useState(false);

  const [dashboardSummary, setDashboardSummary] = useState(null);
  const [feedPosts, setFeedPosts] = useState([]);
  const [showFeedComposer, setShowFeedComposer] = useState(false);
  const [feedPostType, setFeedPostType] = useState("TEXT");
  const [feedContent, setFeedContent] = useState("");
  const [feedAuthor, setFeedAuthor] = useState("");
  const [feedImage, setFeedImage] = useState(null);
  const [feedImageCaption, setFeedImageCaption] = useState("");
  const [feedCopyTag, setFeedCopyTag] = useState("");
  const [feedCopyAuthor, setFeedCopyAuthor] = useState("");
  const [feedCopyText, setFeedCopyText] = useState("");
  const [isCreatingFeedPost, setIsCreatingFeedPost] = useState(false);
  const [dashboardCalendarDate, setDashboardCalendarDate] = useState(new Date());

  const [handbookDocs, setHandbookDocs] = useState([]);
  const [selectedHandbookDoc, setSelectedHandbookDoc] = useState(null);
  const [handbookUpload, setHandbookUpload] = useState(null);
  const [newHandbookSubject, setNewHandbookSubject] = useState("");
  const [isUploadingHandbook, setIsUploadingHandbook] = useState(false);

  const [adminBalanceForm, setAdminBalanceForm] = useState({
    employeeId: "",
    appliedLeave: "",
    opening: "",
    generated: "",
    utilized: "",
    closingBalance: "",
    leaveWithoutPay: "",
    earnedLeave: "",
    casualSickLeave: "",
  });
  const [isSavingBalance, setIsSavingBalance] = useState(false);

  const employeeName =
    dashboardSummary?.employeeName || employeeProfile?.name || "Employee";
  const employeeId =
    leaveBalanceRaw?.employeeId || employeeProfile?.employeeId || "—";
  const todayIsoDate = formatDateToIso(new Date());
  const isSelectedDateToday =
    formatDateToIso(arSelectedDate) === todayIsoDate;

  const loadTodayAttendance = useCallback(async () => {
    try {
      const detail = await fetchAttendanceByDate(todayIsoDate);
      setTodayAttendance(detail);

      if (isSelectedDateToday) {
        setAttendanceDayDetail(detail);
      }
    } catch (error) {
      setSectionError(
        getApiErrorMessage(error, "Failed to load today's attendance."),
      );
    }
  }, [todayIsoDate, isSelectedDateToday]);

  const handleMarkAttendance = async (action) => {
    setIsMarkingAttendance(true);

    try {
      const result = await markAttendance({
        action,
        date: todayIsoDate,
        timestamp: new Date().toISOString(),
      });

      setTodayAttendance(result);

      if (isSelectedDateToday) {
        setAttendanceDayDetail(result);
      }

      const punches = await fetchAttendancePunches({ limit: 10 });
      setAttendancePunches(punches);

      window.alert(
        action === "CHECK_IN"
          ? `Checked in at ${formatPunchTime(result.checkInTime)}`
          : `Checked out at ${formatPunchTime(result.checkOutTime)}`,
      );
    } catch (error) {
      window.alert(getApiErrorMessage(error, "Failed to mark attendance."));
    } finally {
      setIsMarkingAttendance(false);
    }
  };

  const renderMarkAttendancePanel = () => {
    const canCheckIn = !todayAttendance?.checkInTime;
    const canCheckOut =
      Boolean(todayAttendance?.checkInTime) && !todayAttendance?.checkOutTime;

    return (
      <div className="management_Panel management_Mark_Attendance">
        <h4 className="management_Mark_Attendance_Title">
          <strong>Mark attendance</strong>
        </h4>
        <p className="management_Mark_Attendance_Date">
          Today: {formatIsoDateDisplay(todayIsoDate)}
        </p>
        <div className="management_Mark_Attendance_Times">
          <p>
            Check in: {formatPunchTime(todayAttendance?.checkInTime)}
          </p>
          <p>
            Check out: {formatPunchTime(todayAttendance?.checkOutTime)}
          </p>
          <p>
            Status:{" "}
            {todayAttendance?.status
              ? mapAttendanceStatusLabel(todayAttendance.status)
              : "Not marked"}
          </p>
        </div>
        <div className="management_Mark_Attendance_Actions">
          <CustomButton
            title={isMarkingAttendance ? "Saving..." : "Check In"}
            className="submitBtn management_Mark_Button"
            onClick={() => handleMarkAttendance("CHECK_IN")}
            disabled={!canCheckIn || isMarkingAttendance}
          />
          <CustomButton
            title={isMarkingAttendance ? "Saving..." : "Check Out"}
            className="submitBtn management_Mark_Button"
            onClick={() => handleMarkAttendance("CHECK_OUT")}
            disabled={!canCheckOut || isMarkingAttendance}
          />
        </div>
      </div>
    );
  };

  const loadHolidays = useCallback(async () => {
    try {
      const items = await fetchHolidayList();
      setHolidays(items.length ? items : DUMMY_HOLIDAYS);
    } catch {
      setHolidays(DUMMY_HOLIDAYS);
    }
  }, []);

  const loadEmployeeProfile = useCallback(async () => {
    try {
      const profile = await fetchEmployeeMe();
      setEmployeeProfile(profile);
    } catch (error) {
      setSectionError(getApiErrorMessage(error, "Failed to load employee profile."));
    }
  }, []);

  const loadLeaveBalance = useCallback(async () => {
    try {
      const balance = await fetchLeaveBalance();
      setLeaveBalanceRaw(balance);
      setLeaveBalanceCards(mapLeaveBalanceToCards(balance));
      return balance;
    } catch (error) {
      setSectionError(
        getApiErrorMessage(error, "Failed to load leave balance."),
      );
      return null;
    }
  }, []);

  const loadLeaveSection = useCallback(async () => {
    setLoading(true);
    setSectionError("");

    try {
      const punchRange = getCalendarPunchRange();
      const [balance, history, adminRequests, punches] = await Promise.all([
        fetchLeaveBalance(),
        fetchMyLeaveHistory(),
        isAdmin ? fetchAllLeaveRequests() : Promise.resolve([]),
        fetchCalendarPunchesSafe(punchRange),
      ]);

      setLeaveBalanceRaw(balance);
      setLeaveBalanceCards(mapLeaveBalanceToCards(balance));
      setUserLeaveHistory(history);
      setAdminLeaveRequests(adminRequests);
      setCalendarPunches(punches);
    } catch (error) {
      setSectionError(getApiErrorMessage(error, "Failed to load leave data."));
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  const loadAttendanceSection = useCallback(async () => {
    setLoading(true);
    setSectionError("");

    try {
      const punchRange = getCalendarPunchRange();
      const [balance, pending, punches, history, calendarData] =
        await Promise.all([
          fetchArBalance(),
          isAdmin
            ? fetchAllArRequests({ status: "PENDING" })
            : fetchMyArRequests("PENDING"),
          fetchAttendancePunches({ limit: 10 }),
          fetchMyLeaveHistory(),
          fetchCalendarPunchesSafe(punchRange),
        ]);

      setArBalance(balance);
      setPendingArRequests(pending);
      setAttendancePunches(punches);
      setUserLeaveHistory(history);
      setCalendarPunches(calendarData);
    } catch (error) {
      setSectionError(
        getApiErrorMessage(error, "Failed to load attendance data."),
      );
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  const loadDashboardSection = useCallback(async () => {
    setLoading(true);
    setSectionError("");

    try {
      const punchRange = getCalendarPunchRange();
      const [summary, posts, history, punches, balance] = await Promise.all([
        fetchDashboardSummary(),
        fetchFeedPosts(),
        fetchMyLeaveHistory(),
        fetchCalendarPunchesSafe(punchRange),
        fetchLeaveBalance(),
      ]);

      setDashboardSummary(summary);
      setFeedPosts(posts.map(mapFeedPostForDisplay));
      setUserLeaveHistory(history);
      setCalendarPunches(punches);
      setLeaveBalanceRaw(balance);
      setLeaveBalanceCards(mapLeaveBalanceToCards(balance));
    } catch (error) {
      setSectionError(getApiErrorMessage(error, "Failed to load dashboard."));
    } finally {
      setLoading(false);
    }
  }, []);

  const loadHandbookSection = useCallback(async () => {
    setLoading(true);
    setSectionError("");

    try {
      const documents = await fetchHandbookDocuments();
      setHandbookDocs(documents);
      setSelectedHandbookDoc((current) => current || documents[0] || null);
    } catch (error) {
      setSectionError(getApiErrorMessage(error, "Failed to load HR handbook."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeSection === "dashboard" || activeSection === "attendance") {
      loadTodayAttendance();
    }
  }, [activeSection, loadTodayAttendance]);

  useEffect(() => {
    loadHolidays();
    loadEmployeeProfile();
    loadLeaveBalance();
  }, [loadHolidays, loadEmployeeProfile, loadLeaveBalance]);

  useEffect(() => {
    if (activeSection === "leave") {
      loadLeaveSection();
    }

    if (activeSection === "attendance") {
      loadAttendanceSection();
    }

    if (activeSection === "dashboard") {
      loadDashboardSection();
    }

    if (activeSection === "handbook") {
      loadHandbookSection();
    }
  }, [
    activeSection,
    loadLeaveSection,
    loadAttendanceSection,
    loadDashboardSection,
    loadHandbookSection,
  ]);

  useEffect(() => {
    if (activeSection !== "attendance") {
      return;
    }

    const loadDayDetail = async () => {
      const selectedIsoDate = formatDateToIso(arSelectedDate);

      if (!selectedIsoDate) {
        return;
      }

      try {
        const dayDetail = await fetchAttendanceByDate(selectedIsoDate);
        setAttendanceDayDetail(dayDetail);
      } catch (error) {
        setSectionError(
          getApiErrorMessage(error, "Failed to load attendance for selected date."),
        );
      }
    };

    loadDayDetail();
  }, [activeSection, arSelectedDate]);

  const calendarHolidays = useMemo(() => {
    if (holidays.length >= 10) {
      return holidays;
    }

    const merged = [...holidays];
    const existingDates = new Set(holidays.map((item) => item.date));

    DUMMY_HOLIDAYS.forEach((holiday) => {
      if (!existingDates.has(holiday.date)) {
        merged.push(holiday);
      }
    });

    return merged.slice(0, 10);
  }, [holidays]);

  const calendarDayStatusMap = useMemo(() => {
    const statusMap = buildCalendarDayStatusMap({
      leaveHistory: userLeaveHistory,
      punches: calendarPunches,
      todayIso: todayIsoDate,
    });

    return Object.fromEntries(statusMap);
  }, [userLeaveHistory, calendarPunches, todayIsoDate]);

  const calendarDayTooltipMap = useMemo(
    () => buildCalendarDayTooltipMap({ leaveHistory: userLeaveHistory }),
    [userLeaveHistory],
  );

  const showLeaveBalanceCards = hasLeaveBalanceDetails(leaveBalanceRaw);

  const filteredAdminLeaveRequests = useMemo(() => {
    const employeeQuery = adminLeaveFilterEmployeeId.trim().toLowerCase();
    const statusFilter = adminLeaveFilterStatus;
    const startDateFilter = adminLeaveFilterStartDate
      ? formatDateToIso(adminLeaveFilterStartDate)
      : "";

    return adminLeaveRequests.filter((row) => {
      const matchesEmployee =
        !employeeQuery ||
        String(row.employeeId || "")
          .toLowerCase()
          .includes(employeeQuery);

      const matchesStatus =
        statusFilter === "ALL" || row.status === statusFilter;

      const matchesStartDate =
        !startDateFilter || row.startDate === startDateFilter;

      return matchesEmployee && matchesStatus && matchesStartDate;
    });
  }, [
    adminLeaveRequests,
    adminLeaveFilterEmployeeId,
    adminLeaveFilterStatus,
    adminLeaveFilterStartDate,
  ]);

  const adminLeaveEmployeeIds = useMemo(
    () =>
      [...new Set(adminLeaveRequests.map((row) => row.employeeId).filter(Boolean))].sort(),
    [adminLeaveRequests],
  );

  const renderLeaveBalanceCards = (className = "") => {
    if (!showLeaveBalanceCards) {
      return null;
    }

    return (
      <div className={`leave_Type ${className}`.trim()}>
        {leaveBalanceCards.map((card) => (
          <LeaveCard
            key={card.leaveType}
            className="card_Width"
            leaveNos={card.leaveNos}
            leaveType={card.leaveType}
          />
        ))}
      </div>
    );
  };

  const upcomingHoliday = useMemo(() => {
    if (!calendarHolidays.length) {
      return null;
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const sorted = [...calendarHolidays].sort((left, right) => {
      const leftParts = left.date.split("-").map(Number);
      const rightParts = right.date.split("-").map(Number);
      const leftDate = new Date(leftParts[2], leftParts[1] - 1, leftParts[0]);
      const rightDate = new Date(rightParts[2], rightParts[1] - 1, rightParts[0]);

      return leftDate - rightDate;
    });

    return sorted.find((holiday) => {
      const parts = holiday.date.split("-").map(Number);
      const holidayDate = new Date(parts[2], parts[1] - 1, parts[0]);

      return holidayDate >= today;
    });
  }, [calendarHolidays]);

  const handleApplyLeave = async () => {
    if (!leaveStartDate || !leaveEndDate) {
      window.alert("Please select start and end dates.");
      return;
    }

    setIsSubmittingLeave(true);

    try {
      await applyLeaveRequest({
        startDate: formatDateToIso(leaveStartDate),
        endDate: formatDateToIso(leaveEndDate),
        leaveType,
        reason: leaveReason.trim() || undefined,
      });

      setLeaveStartDate(null);
      setLeaveEndDate(null);
      setLeaveReason("");
      setLeaveAttachment(null);
      window.alert("Leave request submitted successfully.");
      await loadLeaveSection();
    } catch (error) {
      window.alert(getApiErrorMessage(error, "Failed to submit leave request."));
    } finally {
      setIsSubmittingLeave(false);
    }
  };

  const handleSubmitAr = async () => {
    if (!arComments.trim()) {
      window.alert("Comments are required for attendance regularization.");
      return;
    }

    setIsSubmittingAr(true);

    try {
      await submitArRequest({
        requestType: arRequestType,
        fromDate: formatDateToIso(arFromDate),
        toDate: formatDateToIso(arToDate),
        startTime: `${arStartHour.padStart(2, "0")}:${arStartMinute.padStart(2, "0")}`,
        endTime: `${arEndHour.padStart(2, "0")}:${arEndMinute.padStart(2, "0")}`,
        comments: arComments.trim(),
      });

      setArComments("");
      setArAttachment(null);
      window.alert("Attendance regularization request submitted.");
      await loadAttendanceSection();
    } catch (error) {
      window.alert(getApiErrorMessage(error, "Failed to submit AR request."));
    } finally {
      setIsSubmittingAr(false);
    }
  };

  const handleHandbookUpload = async () => {
    if (!isAdmin) {
      return;
    }

    if (!newHandbookSubject.trim() || !handbookUpload) {
      window.alert("Enter a subject and choose a PDF file.");
      return;
    }

    setIsUploadingHandbook(true);

    try {
      const fileUrl = await readFileAsDataUrl(handbookUpload);

      await uploadHandbookDocument({
        subject: newHandbookSubject.trim(),
        fileUrl,
        fileName: handbookUpload.name,
      });

      setNewHandbookSubject("");
      setHandbookUpload(null);
      window.alert("Handbook document uploaded successfully.");
      await loadHandbookSection();
    } catch (error) {
      window.alert(getApiErrorMessage(error, "Failed to upload handbook document."));
    } finally {
      setIsUploadingHandbook(false);
    }
  };

  const resetFeedComposer = () => {
    setFeedPostType("TEXT");
    setFeedContent("");
    setFeedAuthor("");
    setFeedImage(null);
    setFeedImageCaption("");
    setFeedCopyTag("");
    setFeedCopyAuthor("");
    setFeedCopyText("");
  };

  const handleCreateFeedPost = async () => {
    if (!feedContent.trim()) {
      window.alert("Post content is required.");
      return;
    }

    if (feedPostType === "COPY" && !feedCopyText.trim()) {
      window.alert("Original copy text is required for reposts.");
      return;
    }

    setIsCreatingFeedPost(true);

    try {
      const payload = {
        type: feedPostType,
        content: feedContent.trim(),
        author: feedAuthor.trim() || employeeName,
      };

      if (feedPostType === "IMAGE" && feedImage) {
        payload.imageUrl = await readFileAsDataUrl(feedImage);
        payload.imageAlt = feedImage.name;
        payload.imageCaption = feedImageCaption.trim() || undefined;
      }

      if (feedPostType === "COPY") {
        payload.copyPost = {
          tag: feedCopyTag.trim() || undefined,
          originalAuthor: feedCopyAuthor.trim() || "Unknown",
          originalContent: feedCopyText.trim(),
        };
      }

      const created = await createFeedPost(payload);
      setFeedPosts((current) => [
        mapFeedPostForDisplay(created),
        ...current,
      ]);
      resetFeedComposer();
      setShowFeedComposer(false);
      window.alert("Feed post published successfully.");
    } catch (error) {
      window.alert(getApiErrorMessage(error, "Failed to create feed post."));
    } finally {
      setIsCreatingFeedPost(false);
    }
  };

  const handleLeaveStatusChange = async (requestId, status) => {
    try {
      const updated = await updateLeaveRequestStatus(requestId, status);
      setAdminLeaveRequests((current) =>
        current.map((row) =>
          row.requestId === requestId ? { ...row, ...updated } : row,
        ),
      );
    } catch (error) {
      window.alert(getApiErrorMessage(error, "Failed to update leave status."));
    }
  };

  const handleLeaveTypeChange = async (requestId, leaveTypeValue) => {
    try {
      const updated = await updateLeaveRequestType(requestId, leaveTypeValue);
      setAdminLeaveRequests((current) =>
        current.map((row) =>
          row.requestId === requestId ? { ...row, ...updated } : row,
        ),
      );
    } catch (error) {
      window.alert(getApiErrorMessage(error, "Failed to update leave type."));
    }
  };

  const handleArRequestClick = (request) => {
    setSelectedArRequest(request);
    setShowArModal(true);
  };

  const handleArStatusUpdate = async (status) => {
    if (!selectedArRequest?.requestId) {
      return;
    }

    setIsUpdatingAr(true);

    try {
      const updated = await updateArRequestStatus(
        selectedArRequest.requestId,
        status,
      );

      setPendingArRequests((current) =>
        current
          .map((row) =>
            row.requestId === updated.requestId ? { ...row, ...updated } : row,
          )
          .filter((row) => row.status === "PENDING"),
      );
      setShowArModal(false);
      setSelectedArRequest(null);
      await loadAttendanceSection();
    } catch (error) {
      window.alert(getApiErrorMessage(error, "Failed to update AR request."));
    } finally {
      setIsUpdatingAr(false);
    }
  };

  const handleAdminBalanceFieldChange = (field, value) => {
    setAdminBalanceForm((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const handleSaveLeaveBalance = async () => {
    if (!adminBalanceForm.employeeId.trim()) {
      window.alert("Employee ID is required.");
      return;
    }

    setIsSavingBalance(true);

    try {
      const payload = {
        employeeId: adminBalanceForm.employeeId.trim(),
      };

      [
        "appliedLeave",
        "opening",
        "generated",
        "utilized",
        "closingBalance",
        "leaveWithoutPay",
        "earnedLeave",
        "casualSickLeave",
      ].forEach((field) => {
        if (adminBalanceForm[field] !== "") {
          payload[field] = Number(adminBalanceForm[field]);
        }
      });

      await updateLeaveBalanceAdmin(payload);
      window.alert("Leave balance updated successfully.");
      await loadLeaveBalance();
      await loadLeaveSection();
    } catch (error) {
      window.alert(getApiErrorMessage(error, "Failed to update leave balance."));
    } finally {
      setIsSavingBalance(false);
    }
  };

  const renderLeaveSection = () => (
    <div className="management_Section">
      {isAdmin && (
        <>
          <h4 className="management_Section_Subtitle">Leave requests for approval</h4>
          <div className="management_Leave_Filters">
            <Form.Group>
              <Form.Label>Emp.ID</Form.Label>
              <Form.Control
                list="admin-leave-employee-ids"
                placeholder="Filter by employee ID"
                value={adminLeaveFilterEmployeeId}
                onChange={(event) =>
                  setAdminLeaveFilterEmployeeId(event.target.value)
                }
              />
              <datalist id="admin-leave-employee-ids">
                {adminLeaveEmployeeIds.map((employeeIdValue) => (
                  <option key={employeeIdValue} value={employeeIdValue} />
                ))}
              </datalist>
            </Form.Group>
            <Form.Group>
              <Form.Label>Status</Form.Label>
              <CustomDropdown
                dropDownItems={LEAVE_STATUS_FILTER_OPTIONS}
                placeholder={
                  LEAVE_STATUS_FILTER_OPTIONS.find(
                    (item) => item.id === adminLeaveFilterStatus,
                  )?.value || "All statuses"
                }
                selectedValue={LEAVE_STATUS_FILTER_OPTIONS.find(
                  (item) => item.id === adminLeaveFilterStatus,
                )}
                onSelect={(selected) =>
                  setAdminLeaveFilterStatus(selected?.id || "ALL")
                }
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Start date</Form.Label>
              <DatePicker
                selected={adminLeaveFilterStartDate}
                onChange={setAdminLeaveFilterStartDate}
                placeholderText="Filter by start date"
                className="form-control"
                dateFormat="dd/MM/yyyy"
                isClearable
              />
            </Form.Group>
            <CustomButton
              title="Clear filters"
              onClick={() => {
                setAdminLeaveFilterEmployeeId("");
                setAdminLeaveFilterStatus("ALL");
                setAdminLeaveFilterStartDate(null);
              }}
            />
          </div>
          <div className="contact_Table management_Table">
            <ul className="header">
              <li>Emp.ID</li>
              <li>Emp.Name</li>
              <li>Start date</li>
              <li>End date</li>
              <li>Leave type</li>
              <li>Reason</li>
              <li>Status</li>
            </ul>
            {filteredAdminLeaveRequests.map((row) => (
              <ul className="data_List" key={row.requestId}>
                <li>{row.employeeId}</li>
                <li>{row.employeeName}</li>
                <li>{formatIsoDateDisplay(row.startDate)}</li>
                <li>{formatIsoDateDisplay(row.endDate)}</li>
                <li className="leave">
                  <CustomDropdown
                    dropDownItems={LEAVE_TYPE_OPTIONS}
                    placeholder={mapLeaveTypeLabel(row.leaveType)}
                    selectedValue={LEAVE_TYPE_OPTIONS.find(
                      (item) => item.id === row.leaveType,
                    )}
                    onSelect={(selected) =>
                      handleLeaveTypeChange(row.requestId, selected?.id)
                    }
                  />
                </li>
                <li className="service">{row.reason}</li>
                <li className="status">
                  <CustomDropdown
                    dropDownItems={LEAVE_STATUS_OPTIONS}
                    placeholder={
                      LEAVE_STATUS_OPTIONS.find((item) => item.id === row.status)
                        ?.value || row.status
                    }
                    selectedValue={LEAVE_STATUS_OPTIONS.find(
                      (item) => item.id === row.status,
                    )}
                    onSelect={(selected) =>
                      handleLeaveStatusChange(row.requestId, selected?.id)
                    }
                  />
                </li>
              </ul>
            ))}
            {!adminLeaveRequests.length && (
              <p className="management_Help_Text management_Table_Empty">
                No leave requests found.
              </p>
            )}
            {adminLeaveRequests.length > 0 &&
              !filteredAdminLeaveRequests.length && (
                <p className="management_Help_Text management_Table_Empty">
                  No leave requests match the selected filters.
                </p>
              )}
          </div>

          <div className="management_Panel management_Admin_Balance">
            <h4>Update employee leave balance</h4>
            <div className="management_Admin_Balance_Grid">
              <Form.Control
                placeholder="Employee ID"
                value={adminBalanceForm.employeeId}
                onChange={(event) =>
                  handleAdminBalanceFieldChange("employeeId", event.target.value)
                }
              />
              {[
                "appliedLeave",
                "opening",
                "generated",
                "utilized",
                "closingBalance",
                "leaveWithoutPay",
                "earnedLeave",
                "casualSickLeave",
              ].map((field) => (
                <Form.Control
                  key={field}
                  type="number"
                  placeholder={field.replace(/([A-Z])/g, " $1")}
                  value={adminBalanceForm[field]}
                  onChange={(event) =>
                    handleAdminBalanceFieldChange(field, event.target.value)
                  }
                />
              ))}
            </div>
            <CustomButton
              title={isSavingBalance ? "Saving..." : "Save leave balance"}
              onClick={handleSaveLeaveBalance}
            />
          </div>
        </>
      )}

      <div className="leave_Main">
        {renderLeaveBalanceCards()}

        <div className="user_Details">
          <div className="name_Section">
            <div className="common_Details">
              <span className="common">Name</span>
              <span className="bold">{employeeName}</span>
            </div>
            <div className="common_Details">
              <span className="common">Employee Id</span>
              <span className="bold">{employeeId}</span>
            </div>
          </div>
          <div className="button_Bottom">
            <CustomButton
              title={showApplyLeave ? "View leave history" : "Apply Leave"}
              onClick={() => setShowApplyLeave(!showApplyLeave)}
            />
          </div>
        </div>

        {!showApplyLeave ? (
          <div className="leave_Table">
            <ul className="header">
              <li className="date">Start date</li>
              <li className="date">End date</li>
              <li className="leave">Leave type</li>
              <li className="reason">Reason</li>
              <li className="status">Status</li>
            </ul>
            {userLeaveHistory.map((row) => (
              <ul className="data_List" key={row.requestId}>
                <li className="date">{formatIsoDateDisplay(row.startDate)}</li>
                <li className="date">{formatIsoDateDisplay(row.endDate)}</li>
                <li className="leave">{row.leaveType}</li>
                <li className="reason">{row.reason}</li>
                <li className="status">
                  {LEAVE_STATUS_OPTIONS.find((item) => item.id === row.status)
                    ?.value || row.status}
                </li>
              </ul>
            ))}
            {!userLeaveHistory.length && (
              <p className="management_Help_Text management_Table_Empty">
                No leave applications found.
              </p>
            )}
          </div>
        ) : (
          <div className="apply_Leave_Section">
            <Label title="Apply Leave" />
            <div className="management_Leave_Type_Row">
              <CustomDropdown
                dropDownItems={LEAVE_TYPE_OPTIONS}
                placeholder="Leave type"
                selectedValue={LEAVE_TYPE_OPTIONS.find(
                  (item) => item.id === leaveType,
                )}
                onSelect={(selected) => setLeaveType(selected?.id || "PAID")}
              />
            </div>
            <div className="date_Section">
              <div className="date">
                <DatePicker
                  selected={leaveStartDate}
                  onChange={setLeaveStartDate}
                  placeholderText="Select start date"
                />
                <i className="icon-calendar iconCalendar" />
              </div>
              <div className="date">
                <DatePicker
                  selected={leaveEndDate}
                  onChange={setLeaveEndDate}
                  placeholderText="Select end date"
                  minDate={leaveStartDate || undefined}
                />
                <i className="icon-calendar iconCalendar" />
              </div>
            </div>
            <textarea
              className="form-control heightText"
              placeholder="Write a reason for leave (Optional)"
              value={leaveReason}
              onChange={(event) => setLeaveReason(event.target.value)}
            />
            <div className="attached_Upload">
              <div className="file-upload-wrapper" data-text="Upload attachment">
                <i className="icon-attached addIcon" />
                <input
                  type="file"
                  className="file-upload-field"
                  onChange={(event) =>
                    setLeaveAttachment(event.target.files?.[0] || null)
                  }
                />
              </div>
              {leaveAttachment && (
                <p className="attachment_Name">{leaveAttachment.name}</p>
              )}
            </div>
            <div className="leave_Bottom">
              <CustomButton
                title="Cancel"
                onClick={() => {
                  setLeaveStartDate(null);
                  setLeaveEndDate(null);
                  setLeaveReason("");
                  setLeaveAttachment(null);
                }}
              />
              <CustomButton
                title={isSubmittingLeave ? "Submitting..." : "Apply"}
                onClick={handleApplyLeave}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const renderAttendanceSection = () => (
    <div className="management_Section management_Attendance">
      <div className="management_Attendance_Header">
        <Label title="Attendance Regularization Request" className="heading" />
        <button
          type="button"
          className="management_Link_Button"
          onClick={() => setActiveSection("handbook")}
        >
          View HR Handbook
        </button>
      </div>
      <p className="management_Help_Text">
        Select day(s) for which you wish to apply for attendance regularization
        request.
      </p>
      <div className="management_Attendance_Layout">
        <div className="management_Attendance_Calendar">
          <ManagementCalendar
            holidays={calendarHolidays}
            dayStatusMap={calendarDayStatusMap}
            dayTooltipMap={calendarDayTooltipMap}
            selectedDate={arSelectedDate}
            onChange={(date) => {
              setArSelectedDate(date);
              setArFromDate(date);
              setArToDate(date);
            }}
          />
          <div className="management_List_Panel management_Ar_Pending">
            <div className="management_Ar_Pending_Header">
              <h5>Pending AR Requests ({pendingArRequests.length})</h5>
              <span className="management_Help_Text">
                {isAdmin
                  ? "All pending requests — click to review"
                  : "Your pending requests"}
              </span>
            </div>
            <ul className="management_Simple_List management_Ar_List">
              {pendingArRequests.map((request) => (
                <li key={request.requestId}>
                  <button
                    type="button"
                    className="management_Ar_List_Item"
                    onClick={() => handleArRequestClick(request)}
                  >
                    <strong>{formatIsoDateDisplay(request.date)}</strong> —{" "}
                    {mapArRequestTypeLabel(request.requestType)} (
                    {request.fromTime} - {request.toTime}) — {request.status}
                    {isAdmin && request.employeeId && (
                      <span className="management_Ar_Employee">
                        Emp: {request.employeeId}
                      </span>
                    )}
                    <span>{request.comments}</span>
                  </button>
                </li>
              ))}
              {!pendingArRequests.length && (
                <li className="management_Help_Text">No pending requests.</li>
              )}
            </ul>
          </div>
        </div>

        <div className="management_Attendance_Form">
          <h4>Request for {toDisplayDate(arSelectedDate)}</h4>
          <div className="management_Shift_Info">
            <p>
              Shift: {attendanceDayDetail?.shiftStart || "09:00"} -{" "}
              {attendanceDayDetail?.shiftEnd || "18:00"}
            </p>
            <p>
              Check in time: {formatPunchTime(attendanceDayDetail?.checkInTime)}
            </p>
            <p>
              Check out time:{" "}
              {formatPunchTime(attendanceDayDetail?.checkOutTime)}
            </p>
            <button
              type="button"
              className="management_Link_Button"
              onClick={() => setShowPunchHistory(!showPunchHistory)}
            >
              View Punches
            </button>
          </div>
          {showPunchHistory && (
            <div className="management_List_Panel">
              <h5>Recent Punch History ({attendancePunches.length})</h5>
              <ul className="management_Simple_List">
                {attendancePunches.map((punch) => (
                  <li key={punch.punchId}>
                    <strong>{formatIsoDateDisplay(punch.date)}</strong> — In:{" "}
                    {formatPunchTime(punch.checkIn)}, Out:{" "}
                    {formatPunchTime(punch.checkOut)} —{" "}
                    {mapAttendanceStatusLabel(punch.status)}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <Form.Group className="mb-3">
            <Form.Label>Request Type</Form.Label>
            <div className="management_Radio_Group">
              {AR_REQUEST_TYPES.map((type) => (
                <Form.Check
                  key={type.id}
                  type="radio"
                  name="arRequestType"
                  id={`ar-${type.id}`}
                  label={type.label}
                  checked={arRequestType === type.id}
                  onChange={() => setArRequestType(type.id)}
                />
              ))}
            </div>
          </Form.Group>

          <div className="management_Date_Row">
            <Form.Group>
              <Form.Label>From date</Form.Label>
              <DatePicker
                selected={arFromDate}
                onChange={setArFromDate}
                className="form-control"
                dateFormat="dd/MM/yyyy"
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>To date</Form.Label>
              <DatePicker
                selected={arToDate}
                onChange={setArToDate}
                className="form-control"
                dateFormat="dd/MM/yyyy"
                minDate={arFromDate || undefined}
              />
            </Form.Group>
          </div>

          <div className="management_Time_Row">
            <Form.Group>
              <Form.Label>Start Hours</Form.Label>
              <Form.Control
                value={arStartHour}
                onChange={(event) => setArStartHour(event.target.value)}
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Start Minutes</Form.Label>
              <Form.Control
                value={arStartMinute}
                onChange={(event) => setArStartMinute(event.target.value)}
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>End hours</Form.Label>
              <Form.Control
                value={arEndHour}
                onChange={(event) => setArEndHour(event.target.value)}
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>End Minutes</Form.Label>
              <Form.Control
                value={arEndMinute}
                onChange={(event) => setArEndMinute(event.target.value)}
              />
            </Form.Group>
          </div>

          <div className="management_Info_Note">
            Note: You are marking AR for 9 hours 0 minutes.
          </div>

          <Form.Group className="mb-3">
            <Form.Label>* Comments</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={arComments}
              onChange={(event) => setArComments(event.target.value)}
            />
          </Form.Group>

          <div className="attached_Upload">
            <div className="file-upload-wrapper" data-text="Upload file">
              <i className="icon-attached addIcon" />
              <input
                type="file"
                className="file-upload-field"
                onChange={(event) =>
                  setArAttachment(event.target.files?.[0] || null)
                }
              />
            </div>
          </div>

          <div className="management_Form_Footer">
            <span>
              AR balance: {arBalance?.remaining ?? "—"}/Monthly
            </span>
            <CustomButton
              title={isSubmittingAr ? "Submitting..." : "Submit"}
              onClick={handleSubmitAr}
            />
          </div>
        </div>
      </div>
    </div>
  );

  const renderDashboardSection = () => (
    <div className="management_Section management_Dashboard">
      <div className="management_Dashboard_Grid">
        <aside className="management_Dashboard_Left">
          {renderMarkAttendancePanel()}
          <div className="management_Panel">
            <h4>Hello, {dashboardSummary?.employeeName || employeeName}!</h4>
          </div>
        </aside>

        <section className="management_Dashboard_Feed">
          <div className="management_Feed_Header">
            <h4>Feed</h4>
            {isAdmin && (
              <CustomButton
                title={showFeedComposer ? "Close" : "Create post"}
                onClick={() => setShowFeedComposer(!showFeedComposer)}
              />
            )}
          </div>

          {isAdmin && showFeedComposer && (
            <div className="management_Feed_Composer">
              <div className="management_Feed_Composer_Row">
                <CustomDropdown
                  dropDownItems={FEED_POST_TYPES}
                  placeholder="Post type"
                  selectedValue={FEED_POST_TYPES.find(
                    (item) => item.id === feedPostType,
                  )}
                  onSelect={(selected) =>
                    setFeedPostType(selected?.id || "TEXT")
                  }
                />
                <Form.Control
                  placeholder="Author name (optional)"
                  value={feedAuthor}
                  onChange={(event) => setFeedAuthor(event.target.value)}
                />
              </div>
              <Form.Control
                as="textarea"
                rows={3}
                placeholder="Write announcement or update..."
                value={feedContent}
                onChange={(event) => setFeedContent(event.target.value)}
              />
              {feedPostType === "IMAGE" && (
                <div className="management_Feed_Composer_Extras">
                  <Form.Control
                    type="file"
                    accept="image/*"
                    onChange={(event) =>
                      setFeedImage(event.target.files?.[0] || null)
                    }
                  />
                  <Form.Control
                    placeholder="Image caption (optional)"
                    value={feedImageCaption}
                    onChange={(event) =>
                      setFeedImageCaption(event.target.value)
                    }
                  />
                </div>
              )}
              {feedPostType === "COPY" && (
                <div className="management_Feed_Composer_Extras">
                  <Form.Control
                    placeholder="Tag (optional)"
                    value={feedCopyTag}
                    onChange={(event) => setFeedCopyTag(event.target.value)}
                  />
                  <Form.Control
                    placeholder="Original author"
                    value={feedCopyAuthor}
                    onChange={(event) =>
                      setFeedCopyAuthor(event.target.value)
                    }
                  />
                  <Form.Control
                    as="textarea"
                    rows={2}
                    placeholder="Original post content"
                    value={feedCopyText}
                    onChange={(event) => setFeedCopyText(event.target.value)}
                  />
                </div>
              )}
              <div className="management_Feed_Composer_Actions">
                <CustomButton
                  title="Cancel"
                  onClick={() => {
                    resetFeedComposer();
                    setShowFeedComposer(false);
                  }}
                />
                <CustomButton
                  title={isCreatingFeedPost ? "Publishing..." : "Publish"}
                  onClick={handleCreateFeedPost}
                />
              </div>
            </div>
          )}

          {feedPosts.map((post) => renderFeedPost(post))}
        </section>

        <aside className="management_Dashboard_Right">
          <div className="management_Panel">
            <div className="management_Panel_Header">
              <h4>Calendar</h4>
              <button type="button" className="management_Link_Button">
                Go to calendar
              </button>
            </div>
            <ManagementCalendar
              holidays={calendarHolidays}
              dayStatusMap={calendarDayStatusMap}
              dayTooltipMap={calendarDayTooltipMap}
              selectedDate={dashboardCalendarDate}
              onChange={setDashboardCalendarDate}
            />
            <ul className="management_Simple_List management_Holiday_List">
              {calendarHolidays.slice(0, 10).map((holiday) => (
                <li key={holiday.holidayId || holiday.date}>
                  {holiday.date} — {holiday.name} ({holiday.day})
                </li>
              ))}
            </ul>
            {upcomingHoliday && (
              <p className="management_Upcoming_Holiday">
                Next holiday: {upcomingHoliday.name} on {upcomingHoliday.date}
              </p>
            )}
          </div>

          <div className="management_Panel management_Leave_Balance">
            <h4>Leave Balance</h4>
            {renderLeaveBalanceCards("management_Leave_Balance_Cards") || (
              <p className="management_Help_Text">No leave balance data available.</p>
            )}
            <CustomButton
              title="Apply Leave"
              onClick={() => {
                setActiveSection("leave");
                setShowApplyLeave(true);
              }}
            />
          </div>
        </aside>
      </div>
    </div>
  );

  const renderHandbookSection = () => (
    <div className="management_Section management_Handbook">
      <div className="management_Handbook_Header">
        <Label title="HR Handbook" className="heading" />
        {isAdmin && (
          <CustomButton
            title={isUploadingHandbook ? "Uploading..." : "Upload PDF"}
            onClick={handleHandbookUpload}
          />
        )}
      </div>

      {isAdmin && (
        <div className="management_Handbook_Upload">
          <Form.Control
            type="text"
            placeholder="Document subject"
            value={newHandbookSubject}
            onChange={(event) => setNewHandbookSubject(event.target.value)}
          />
          <Form.Control
            type="file"
            accept="application/pdf"
            onChange={(event) =>
              setHandbookUpload(event.target.files?.[0] || null)
            }
          />
          <p className="management_Help_Text">
            Admin can upload company policy PDFs. Users can view them inline.
          </p>
        </div>
      )}

      <div className="management_Handbook_Layout">
        <div className="management_Handbook_List">
          {handbookDocs.map((doc) => (
            <article
              className={`management_Handbook_Card ${
                selectedHandbookDoc?.documentId === doc.documentId
                  ? "is-selected"
                  : ""
              }`}
              key={doc.documentId}
            >
              <div className="management_Handbook_Icon">PDF</div>
              <div>
                <h5>{doc.subject}</h5>
                <p>Published on: {formatIsoDateDisplay(doc.publishedOn)}</p>
                <button
                  type="button"
                  className="management_Link_Button"
                  onClick={() => setSelectedHandbookDoc(doc)}
                >
                  View document
                </button>
              </div>
            </article>
          ))}
        </div>

        <HandbookPdfViewer
          fileUrl={selectedHandbookDoc?.fileUrl}
          subject={selectedHandbookDoc?.subject}
        />
      </div>
    </div>
  );

  const renderArReviewModal = () => (
    <Modal
      show={showArModal}
      onHide={() => {
        setShowArModal(false);
        setSelectedArRequest(null);
      }}
      centered
      className="management_Ar_Modal"
    >
      <Modal.Header closeButton>
        <Modal.Title>Attendance Regularization Request</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {selectedArRequest && (
          <div className="management_Ar_Modal_Body">
            <p>
              <strong>Date:</strong>{" "}
              {formatIsoDateDisplay(selectedArRequest.date)}
            </p>
            {selectedArRequest.employeeId && (
              <p>
                <strong>Employee ID:</strong> {selectedArRequest.employeeId}
              </p>
            )}
            <p>
              <strong>Type:</strong>{" "}
              {mapArRequestTypeLabel(selectedArRequest.requestType)}
            </p>
            <p>
              <strong>Time:</strong> {selectedArRequest.fromTime} -{" "}
              {selectedArRequest.toTime}
            </p>
            <p>
              <strong>Status:</strong> {selectedArRequest.status}
            </p>
            <p>
              <strong>Comments:</strong> {selectedArRequest.comments}
            </p>
          </div>
        )}
      </Modal.Body>
      {isAdmin && selectedArRequest?.status === "PENDING" && (
        <Modal.Footer>
          <CustomButton
            title={isUpdatingAr ? "Updating..." : "Reject"}
            onClick={() => handleArStatusUpdate("REJECTED")}
            disabled={isUpdatingAr}
          />
          <CustomButton
            title={isUpdatingAr ? "Updating..." : "Approve"}
            onClick={() => handleArStatusUpdate("APPROVED")}
            disabled={isUpdatingAr}
          />
        </Modal.Footer>
      )}
    </Modal>
  );

  return (
    <div className="management_Admin_Tab">
      <div className="management_Section_Nav">
        {MANAGEMENT_SECTIONS.map((section) => (
          <button
            key={section.id}
            type="button"
            className={`management_Section_Tab ${
              activeSection === section.id ? "is-active" : ""
            }`}
            onClick={() => setActiveSection(section.id)}
          >
            {labels[section.id] || section.label}
          </button>
        ))}
      </div>

      {loading && <p className="management_Help_Text">Loading...</p>}
      {sectionError && <p className="management_Section_Error">{sectionError}</p>}

      {activeSection === "leave" && renderLeaveSection()}
      {activeSection === "attendance" && renderAttendanceSection()}
      {activeSection === "dashboard" && renderDashboardSection()}
      {activeSection === "handbook" && renderHandbookSection()}
      {renderArReviewModal()}
    </div>
  );
};

export default ManagementAdminTab;
